import type { Persister } from "@tanstack/react-query-persist-client";
import { clear, createStore, del, get, set } from "idb-keyval";

const DB_NAME = "react-query-cache";
const STORE_NAME = "anystore";

// If the IDB database is in a broken state (e.g. store missing after a
// partial cache clear), delete it entirely so it is recreated fresh on
// the next page load rather than crashing on every transaction.
function deleteBrokenDatabase() {
  try {
    indexedDB.deleteDatabase(DB_NAME);
  } catch {
    // Best-effort — nothing more we can do if this also fails
  }
}

// Create an IndexedDB-backed persister compatible with react-query's persist client
// This keeps a small, focused API that saves, restores, removes, or clears the cached client state
export function createIDBPersister(idbValidKey: IDBValidKey = "reactQuery") {
  // Use a dedicated IndexedDB database and store for react-query cache
  const anystore = createStore(DB_NAME, STORE_NAME);
  return {
    // Save the given client state under the provided key
    persistClient: async (client) => {
      try {
        await set(idbValidKey, client, anystore);
      } catch (error) {
        console.warn("🟡 Failed to persist query cache to IDB:", error);
      }
    },
    // Retrieve the client state previously saved for the key.
    // Race against a 3-second timeout so that a hung IDB transaction on mobile
    // wake-up never leaves isRestoring = true indefinitely.
    restoreClient: async () => {
      try {
        const timeout = new Promise<undefined>((resolve) =>
          setTimeout(() => resolve(undefined), 3000),
        );
        return await Promise.race([get(idbValidKey, anystore), timeout]);
      } catch (error) {
        console.warn(
          "🟡 Failed to restore query cache from IDB — resetting database:",
          error,
        );
        deleteBrokenDatabase();
        return undefined;
      }
    },
    // Remove the saved client state for the key
    removeClient: async () => {
      try {
        await del(idbValidKey, anystore);
      } catch (error) {
        console.warn("🟡 Failed to remove query cache from IDB:", error);
      }
    },
    // Clear all entries in the underlying IndexedDB store
    clear: async () => {
      try {
        await clear(anystore);
      } catch (error) {
        console.warn("🟡 Failed to clear query cache from IDB:", error);
      }
    },
  } as Persister;
}
